import { HashtagSourcesApiResponse } from 'api/api.types';
import { useIsDesktopView } from 'deepstash-ui';
import useInfiniteScroll from 'hooks/useInfiniteScroll';
import useAuth from 'providers/hooks/useAuth';
import useLayoutContext from 'providers/hooks/useLayoutContext';
import React, { useEffect, useMemo, useRef } from 'react';
import Layout from 'src/components/layout/Layout';
import TopicMetacontent from 'src/components/metacontent/TopicMetacontent';
import TopicHeaderV2 from 'src/page-components/topic/components/TopicHeaderV2';
import TopicPageContent from 'src/page-components/topic/TopicPageContent';
import { Source } from 'types/models';
import { GAP_SIZE_REM, getRemWidthFromColumns } from 'utils/global';
import { getTopicPageMetadata } from 'utils/metadata.utils';
import { UNSORTED_TOPICS } from 'utils/metatopics.constants';
import { normalizeSource } from 'utils/normalizers/source';

interface TopicPageProps {
  topicTag: string;
  hashtagApi: HashtagSourcesApiResponse;
}

const TopicPageSI: React.FC<TopicPageProps> = ({ hashtagApi }) => {
  const target = useRef<HTMLDivElement>(null);

  const { isLoggedIn } = useAuth();
  const { remeasureLayoutCallbackRef } = useLayoutContext();

  const isDesktopView = useIsDesktopView();

  // moved this up from TopicPage to get here data with all Sources of current topic
  // in order to send to HeadMetadata in Layout a pageDescription that contains the
  // name of one of the sources.
  // Also, if hasthag is null, it will return ErrorPage anyway
  const { data, isFetching, isFirstLoading, hasNextPage } =
    useInfiniteScroll<HashtagSourcesApiResponse>({
      firstPageUrl: `/hashtag/name/${hashtagApi?.hashtag}/?limit=5&offset=0`,
      queryKey: ['hashtag', hashtagApi?.hashtag],
      getKey: (_, previousPageData) => {
        // reached the end
        if (previousPageData && previousPageData.objects.length === 0)
          return undefined;

        const next = previousPageData.meta.next?.replace('/api/v2', '');
        return next;
      },
      target,
      initialData: hashtagApi,
      maxPageCount: !isLoggedIn ? 1 : undefined,
    });

  const dataArray: Source[] =
    data
      ?.map(page => page.objects.map(source => normalizeSource(source)))
      .reduce((prev, val) => prev.concat(val), []) || [];

  // here, i get the shortest title out of all sources. If the title is still
  // longer than 70 characters, it's truncated with ellipsis
  const minTitle = useMemo(() => {
    if (dataArray.length > 0) {
      const minTitleSource = dataArray.reduce((min, source) => {
        if (min === null) {
          return source;
        } else {
          if (source.title.length < min.title.length) {
            return source;
          } else {
            return min;
          }
        }
      });
      return minTitleSource.title.length < 70
        ? minTitleSource.title
        : minTitleSource.title.slice(67) + '...';
    }
    return '';
  }, [dataArray]);

  useEffect(() => {
    remeasureLayoutCallbackRef?.current?.();
  }, [dataArray.length]);

  const allIdeasSection = {
    infiniteScroll: isLoggedIn || !isDesktopView,
    content: (
      <>
        <TopicHeaderV2
          width={{
            base: '100%',
            md: getRemWidthFromColumns(8),
          }}
          mb={GAP_SIZE_REM}
          hashtagSourcesApiResponse={hashtagApi}
        />
        <TopicPageContent
          hashtagSourcesApiResponse={hashtagApi}
          isFetching={isFetching}
          isFirstLoading={isFirstLoading}
          hasNextPage={hasNextPage}
          data={dataArray}
          target={target}
        />
      </>
    ),
    metaContent: <TopicMetacontent hashtagSourcesApiResponse={hashtagApi} />,
    metaContentTopOffset: 96,
    /**
     * If logged in sticky the top stashers and related topics subsections
     * If not logged in sticky the sign up card
     * This prop is only for the desktop view
     */
    metaContentStickyOffset: isLoggedIn ? 32 : -688,
  };

  return (
    <Layout
      pageMetadata={getTopicPageMetadata({
        topicName: hashtagApi.hashtag,
        topicTitle: hashtagApi.hashtag,
        exampleArticleTitle: minTitle,
        pageTitle: hashtagApi.seo_title,
        pageDescription: hashtagApi.seo_description,
      })}
      sections={[allIdeasSection]}
      breadcrumbs={[
        {
          title: 'Home',
          link: '/',
        },
        {
          title:
            UNSORTED_TOPICS[hashtagApi.hashtag]?.title ?? hashtagApi.hashtag,
          link: `/topic/${hashtagApi.hashtag}`,
        },
      ]}
    />
  );
};

export default TopicPageSI;
